/**
 * App-level state: auth snapshot, language, episode + progress session.
 * Lanes consume `useSession()`; only L1 edits this file. API documented in play/README.md ("Session API (L1)").
 */
import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_EPISODE_ID,
  DEFAULT_VISIBLE_EPISODE_IDS,
  carryLocalOnlyProgress,
  sameProgressRow,
  deliverStagedProof as deliverStagedProofDomain,
  shareProof as shareProofDomain,
  type ClientChatOpenMode,
  type EpisodeContent,
  type EpisodeProgress,
  type PhoneItemContent,
  type ProofFeedback,
  type ShareProofResult,
} from '@/domain';
import { type AuthSnapshot, prepareAnonymousAccount } from '@/services/auth';
import { initAnalytics, identify, registerSuper, track } from '@/services/analytics';
import { loadManifest } from '@/services/media';
import { availableLocalizationLocales, loadEpisode, visibleEpisodeIDs as fetchVisibleEpisodeIDs } from '@/services/episodeRepository';
import { isPremium as fetchIsPremium, loadProgress, loadProgressRow, saveProgress } from '@/services/progressRepository';
import { type Catalog, type ContentLocale, type LanguageSelection, type Translate, loadCatalog, makeTranslate, resolveEffectiveLocale, SELECTION_KEY, isContentLocale } from '@/services/i18n';
import { markScreenMounted, markSessionReady } from './boot/bootStore';
import { useViewportClass } from './layout/useViewportClass';
import { readCachedAvailableLocales, refreshAvailableLocales } from './session/availability';
import { fallbackPolicy, loadEpisodeSession, type EpisodeSessionDeps, type LoadedEpisodeSession } from './session/loadEpisodeSession';
import { createProgressSaver, SAVE_FAILED_MESSAGE, type ProgressSaver, type SaveState } from './session/progressSaver';
import { isPlayableEpisode, readSelectedEpisodeID, writeSelectedEpisodeID } from './session/selectedEpisode';

export type { ClientChatOpenMode, SaveState };
export type EpisodeLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

export const LANGUAGE_UNAVAILABLE_MESSAGE = 'That language is unavailable right now. Your current language was kept.';

export interface Session {
  auth: AuthSnapshot;
  locale: ContentLocale;
  selection: LanguageSelection;
  availableLocales: ReadonlySet<string>;
  t: Translate;
  /** Commits only once the current episode reloaded in the new language (strict for a fixed language); else sets `languageError`. */
  setSelection(selection: LanguageSelection): Promise<void>;
  /** Episode currently loaded (undefined on the Home before selection). */
  episode?: EpisodeContent;
  progress?: EpisodeProgress;
  servedLocale?: string;
  /** Dev/preview only: shows content without persisting progress. */
  setEpisode(episode: EpisodeContent | undefined, servedLocale?: string): void;
  /** Single mutation entry. Every change is persisted (chained upsert, rollback + `saveState: 'failed'` on error). */
  setProgress(update: EpisodeProgress | ((p: EpisodeProgress) => EpisodeProgress)): void;
  isPremium: boolean;
  setPremium(v: boolean): void;

  // ---- L1 additions ----
  /** Selected/loaded episode id (`teacheck.selectedEpisodeID`, default EP1). */
  episodeID: string;
  episodeState: EpisodeLoadState;
  /** Last load error (network, unpublished case). Cleared by the next successful load. */
  episodeError?: string;
  /** Published episode ids (RLS), for Home teasers. */
  visibleEpisodeIDs: ReadonlySet<string>;
  /** Loads another case (waits for pending saves); navigates to /play unless `navigate: false`. Resolves false on failure (previous case kept). */
  selectEpisode(id: string, options?: { navigate?: boolean }): Promise<boolean>;
  /** Retries the current case after a failed load. */
  retryEpisodeLoad(): Promise<boolean>;
  /** Reloads the current episode for the effective locale. Strict for a fixed language: on failure keeps the current content and sets `languageError`. */
  reloadForLanguage(): Promise<boolean>;
  languageError?: string;
  clearLanguageError(): void;
  /** "Share proof with {client}" (domain/proof.ts + analytics + chat open). Rejected outcomes set `proofFeedback`. */
  shareProof(item: PhoneItemContent): ShareProofResult | undefined;
  /** Messages app: attach the staged proof. Returns the conversation to show next (proofDelivery.conversationID) when delivered. */
  deliverStagedProof(conversationID?: string, attachmentID?: string): { delivered: boolean; conversationID?: string };
  /** Feedback sheet data for the last rejected proof (phone shell renders it). */
  proofFeedback?: ProofFeedback;
  dismissProofFeedback(): void;
  /** How the chat should animate on its next mount/focus. */
  openMode: ClientChatOpenMode;
  /** Returns the pending open mode and resets it to 'normal'. */
  consumeOpenMode(): ClientChatOpenMode;
  /** Sets `openMode` and brings the chat on screen (navigates to /play unless the split layout already shows it). */
  openClientChat(mode?: ClientChatOpenMode): void;
  saveState: SaveState;
  /** Localized "Progress not saved. Check your connection." while `saveState === 'failed'`. */
  saveErrorMessage?: string;
  dismissSaveError(): void;
}

const SessionContext = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside AppProviders');
  return s;
}

function readSelection(): LanguageSelection {
  try { const v = localStorage.getItem(SELECTION_KEY); return v && (v === 'automatic' || isContentLocale(v)) ? v : 'automatic'; } catch { return 'automatic'; }
}
function writeSelection(v: LanguageSelection) {
  try { localStorage.setItem(SELECTION_KEY, v); } catch { /* ignore */ }
}
function deviceLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return ['en'];
  return navigator.languages?.length ? navigator.languages : [navigator.language];
}
async function translatorFor(locale: ContentLocale): Promise<Translate> {
  // The production en chunk only holds plural forms (identity entries are stripped at build), so the fallback is cheap.
  const [catalog, en] = await Promise.all([loadCatalog(locale), locale === 'en' ? Promise.resolve<Catalog | undefined>(undefined) : loadCatalog('en').catch(() => undefined)]);
  return makeTranslate(locale, catalog, en);
}
const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

const deps: EpisodeSessionDeps = { loadEpisode, loadProgress, loadProgressRow, isPremium: fetchIsPremium };

// One anonymous sign-in per page load, even when StrictMode mounts the provider twice.
let authPromise: Promise<AuthSnapshot> | undefined;
/** Dev previews (/dev/*) render from local JSON; they must not die when Supabase rate-limits anonymous sign-ups. */
const DEV_OFFLINE_AUTH: AuthSnapshot = { userID: '00000000-0000-4000-8000-00000000de00', isAnonymous: true, accessToken: '' };
function isDevPreviewPath(): boolean {
  return import.meta.env.DEV && typeof location !== 'undefined' && location.pathname.startsWith('/dev');
}
function prepareAccountOnce(): Promise<AuthSnapshot> {
  // Dev previews never create Supabase users: each fresh browser would add a junk
  // anonymous account to the production project (and hit the per-IP rate limit).
  if (isDevPreviewPath()) return Promise.resolve(DEV_OFFLINE_AUTH);
  authPromise ??= prepareAnonymousAccount().catch((e: unknown) => {
    authPromise = undefined;
    if (isDevPreviewPath()) {
      console.warn('[dev] anonymous sign-in failed, using offline dev identity:', errorText(e));
      return DEV_OFFLINE_AUTH;
    }
    throw e;
  });
  return authPromise;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const viewport = useViewportClass();
  const [auth, setAuth] = useState<AuthSnapshot>();
  const [ready, setReady] = useState(false);
  const [selection, setSel] = useState<LanguageSelection>(readSelection);
  const [availableLocales, setAvailable] = useState<ReadonlySet<string>>(readCachedAvailableLocales);
  const [visibleIDs, setVisibleIDs] = useState<ReadonlySet<string>>(() => new Set(DEFAULT_VISIBLE_EPISODE_IDS));
  const [locale, setLocale] = useState<ContentLocale>('en');
  const [t, setT] = useState<Translate>(() => makeTranslate('en', {}));
  const [episode, setEp] = useState<EpisodeContent>();
  const [servedLocale, setServed] = useState<string>();
  const [progress, setProg] = useState<EpisodeProgress>();
  const [isPremium, setPremiumState] = useState(false);
  const [episodeID, setEpisodeID] = useState<string>(readSelectedEpisodeID);
  const [episodeState, setEpisodeState] = useState<EpisodeLoadState>('idle');
  const [episodeError, setEpisodeError] = useState<string>();
  const [languageError, setLanguageError] = useState<string>();
  const [proofFeedback, setProofFeedback] = useState<ProofFeedback>();
  const [openMode, setOpenMode] = useState<ClientChatOpenMode>('normal');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [error, setError] = useState<string>();

  // Latest values for imperative callbacks (never read React state inside async flows).
  const authRef = useRef<AuthSnapshot | undefined>(undefined);
  const episodeRef = useRef<EpisodeContent | undefined>(undefined);
  const progressRef = useRef<EpisodeProgress | undefined>(undefined);
  const persistRef = useRef(false); // false for setEpisode() previews
  const episodeLocaleRef = useRef<string | undefined>(undefined); // locale requested for the loaded episode
  const premiumRef = useRef(false);
  const selectionRef = useRef(selection);
  const availableRef = useRef<ReadonlySet<string>>(availableLocales);
  const tRef = useRef(t);
  const viewportRef = useRef(viewport);
  const openModeRef = useRef<ClientChatOpenMode>('normal');
  const startedRef = useRef(new Set<string>());
  const loadTokenRef = useRef(0);
  useEffect(() => { tRef.current = t; }, [t]);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);

  const [saver] = useState<ProgressSaver>(() => createProgressSaver({ save: saveProgress, onStateChange: setSaveState }));

  const applyProgress = useCallback((next: EpisodeProgress | undefined) => {
    progressRef.current = next;
    setProg(next);
  }, []);

  const applyLoaded = useCallback((loaded: LoadedEpisodeSession) => {
    const sameEpisode = episodeRef.current?.id === loaded.episode.id;
    episodeRef.current = loaded.episode;
    episodeLocaleRef.current = loaded.requestedLocale;
    persistRef.current = true;
    premiumRef.current = loaded.isPremium;
    setEp(loaded.episode);
    setServed(loaded.servedLocale);
    applyProgress(sameEpisode ? carryLocalOnlyProgress(progressRef.current, loaded.progress) : loaded.progress);
    setPremiumState(loaded.isPremium);
    setEpisodeID(loaded.episode.id);
    writeSelectedEpisodeID(loaded.episode.id);
    setEpisodeState('loaded');
    setEpisodeError(undefined);
    setProofFeedback(undefined);
    if (loaded.untouched && !startedRef.current.has(loaded.episode.id)) {
      startedRef.current.add(loaded.episode.id);
      track('episode_started', { episode_id: loaded.episode.id });
    }
  }, [applyProgress]);

  const loadInto = useCallback(async (id: string, opts: { coldStart: boolean }): Promise<boolean> => {
    const user = authRef.current;
    if (!user) return false;
    const token = ++loadTokenRef.current;
    setEpisodeState('loading');
    await saver.flush();
    try {
      const loaded = await loadEpisodeSession({
        episodeID: isPlayableEpisode(id) ? id : DEFAULT_EPISODE_ID,
        locale: resolveEffectiveLocale(selectionRef.current, deviceLanguages(), availableRef.current),
        selection: selectionRef.current,
        userID: user.userID,
        allowDefaultFallback: opts.coldStart && !episodeRef.current,
        premiumHint: premiumRef.current,
      }, deps);
      if (token !== loadTokenRef.current) return false;
      saver.invalidate();
      applyLoaded(loaded);
      return true;
    } catch (e) {
      if (token !== loadTokenRef.current) return false;
      setEpisodeError(errorText(e));
      setEpisodeState(episodeRef.current ? 'loaded' : 'failed');
      return false;
    }
  }, [applyLoaded, saver]);

  // Bootstrap. In parallel: auth ∥ media manifest ∥ UI catalog (cached availability), then
  // availability refresh ∥ episode + progress + premium (cached availability). Only when the
  // refreshed availability changes the language is the episode loaded a second time.
  // (App.tsx starts the landing route's chunk at the same time.)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      initAnalytics();
      track('app_opened');
      const coldSelection = selectionRef.current;
      const coldLocale = resolveEffectiveLocale(coldSelection, deviceLanguages(), availableRef.current);
      const coldTranslate = translatorFor(coldLocale).catch(() => undefined);
      // The media manifest only gates the first render (image URLs), not the Supabase queries.
      const manifest = loadManifest().catch(() => undefined);
      const snapshot = await prepareAccountOnce();
      if (cancelled) return;
      identify(snapshot.userID, { is_anonymous: snapshot.isAnonymous });
      authRef.current = snapshot;
      const episodeLoad = loadInto(readSelectedEpisodeID(), { coldStart: true });
      try {
        const { visible, available } = await refreshAvailableLocales({ visibleEpisodeIDs: fetchVisibleEpisodeIDs, availableLocalizationLocales });
        if (cancelled) return;
        availableRef.current = available;
        setAvailable(available);
        setVisibleIDs(visible);
        // A fixed language that is no longer published falls back to automatic (LanguageStore parity).
        const current = selectionRef.current;
        if (current !== 'automatic' && !available.has(current)) {
          selectionRef.current = 'automatic';
          writeSelection('automatic');
          setSel('automatic');
        }
      } catch { /* offline: cached availability is kept */ }
      const effective = resolveEffectiveLocale(selectionRef.current, deviceLanguages(), availableRef.current);
      const translate = (effective === coldLocale ? await coldTranslate : undefined)
        ?? await translatorFor(effective).catch(() => makeTranslate('en', {}));
      if (cancelled) return;
      tRef.current = translate;
      setLocale(effective);
      setT(() => translate);
      setAuth(snapshot);
      await episodeLoad;
      if (cancelled) return;
      if (effective !== coldLocale || selectionRef.current !== coldSelection) await loadInto(readSelectedEpisodeID(), { coldStart: true });
      await manifest;
      // Time-sliced: mounting the first screen (chat, phone) must not stall the launch splash.
      if (!cancelled) startTransition(() => setReady(true));
    })().catch((e: unknown) => { if (!cancelled) setError(errorText(e)); });
    return () => { cancelled = true; };
  }, [loadInto]);

  const reloadForLanguage = useCallback(async (): Promise<boolean> => {
    const current = episodeRef.current;
    if (!current || !persistRef.current) return false;
    const target = resolveEffectiveLocale(selectionRef.current, deviceLanguages(), availableRef.current);
    try {
      const loaded = await loadEpisode(current.id, target, fallbackPolicy(selectionRef.current));
      if (episodeRef.current?.id !== current.id) return false;
      episodeRef.current = loaded.content;
      episodeLocaleRef.current = target;
      setEp(loaded.content);
      setServed(loaded.servedLocale);
      setLanguageError(undefined);
      return true;
    } catch {
      setLanguageError(tRef.current(LANGUAGE_UNAVAILABLE_MESSAGE));
      return false;
    }
  }, []);

  // UI catalog follows the effective locale; the episode follows when it was loaded for another locale.
  useEffect(() => {
    if (!auth) return;
    const effective = resolveEffectiveLocale(selection, deviceLanguages(), availableLocales);
    let cancelled = false;
    translatorFor(effective).then((translate) => {
      if (cancelled) return;
      setLocale(effective);
      setT(() => translate);
      if (episodeRef.current && persistRef.current && episodeLocaleRef.current !== effective) void reloadForLanguage();
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [auth, selection, availableLocales, reloadForLanguage]);

  // Super properties (plan §5.11).
  useEffect(() => {
    registerSuper({ app_language: locale, content_locale: servedLocale ?? locale, viewport_class: viewport });
  }, [locale, servedLocale, viewport]);

  const setProgress = useCallback((update: EpisodeProgress | ((p: EpisodeProgress) => EpisodeProgress)) => {
    const previous = progressRef.current;
    const next = typeof update === 'function' ? (previous ? update(previous) : undefined) : update;
    if (!next || next === previous) return;
    applyProgress(next);
    const ep = episodeRef.current;
    const user = authRef.current;
    if (previous && ep && !previous.solved && next.solved) track('episode_solved', { episode_id: ep.id });
    if (!previous || !ep || !user || !persistRef.current) return;
    // Local-only changes (viewedRequestIDs, unlockedNoteGroups) leave the row untouched: no save.
    if (sameProgressRow(ep, previous, next)) return;
    void saver.enqueue(user.userID, ep, previous, next, {
      isPremiumUnlocked: () => premiumRef.current,
      onRollback(rolledBackEpisodeID, restored) {
        if (episodeRef.current?.id !== rolledBackEpisodeID) return;
        progressRef.current = restored;
        setProg(restored);
      },
    });
  }, [applyProgress, saver]);

  const openClientChat = useCallback((mode: ClientChatOpenMode = 'normal') => {
    openModeRef.current = mode;
    setOpenMode(mode);
    const path = typeof window === 'undefined' ? '' : window.location.pathname;
    const chatVisible = path === '/play' || (viewportRef.current === 'split' && path.startsWith('/play'));
    if (!chatVisible) void navigate('/play');
  }, [navigate]);

  const consumeOpenMode = useCallback((): ClientChatOpenMode => {
    const mode = openModeRef.current;
    if (mode !== 'normal') { openModeRef.current = 'normal'; setOpenMode('normal'); }
    return mode;
  }, []);

  const shareProof = useCallback((item: PhoneItemContent): ShareProofResult | undefined => {
    const ep = episodeRef.current;
    const p = progressRef.current;
    if (!ep || !p) return undefined;
    const result = shareProofDomain(p, ep, undefined, item, tRef.current);
    if (result.outcome === 'noActiveRequest' || !result.request) {
      setProofFeedback(result.feedback);
      return result;
    }
    track('proof_submitted', { episode_id: ep.id, request_id: result.request.id, proof_id: item.proofID ?? 'none', phone_item_id: item.id });
    setProgress(result.progress);
    if (result.completedRequest) {
      track('request_completed', { episode_id: ep.id, request_id: result.completedRequest.id });
      if (result.completedRequest.id === ep.paywallAfterRequestID) {
        track('premium_gate_hit', { episode_id: ep.id, request_id: result.completedRequest.id, gate: 'storyR5' });
      }
    }
    if (result.outcome === 'correct' || result.outcome === 'staged') {
      setProofFeedback(undefined);
      openClientChat(result.openMode);
    } else {
      setProofFeedback(result.feedback);
    }
    return result;
  }, [openClientChat, setProgress]);

  const deliverStagedProof = useCallback((conversationID?: string): { delivered: boolean; conversationID?: string } => {
    const ep = episodeRef.current;
    const p = progressRef.current;
    if (!ep || !p) return { delivered: false };
    const result = deliverStagedProofDomain(p, ep, undefined, conversationID);
    if (!result.completedRequest) return { delivered: false };
    setProgress(result.progress);
    track('request_completed', { episode_id: ep.id, request_id: result.completedRequest.id });
    return { delivered: true, conversationID: result.completedRequest.proofDelivery?.conversationID };
  }, [setProgress]);

  const selectEpisode = useCallback(async (id: string, options: { navigate?: boolean } = {}): Promise<boolean> => {
    if (!isPlayableEpisode(id)) return false;
    const shouldNavigate = options.navigate ?? true;
    if (episodeRef.current?.id === id && persistRef.current) {
      if (shouldNavigate) void navigate('/play');
      return true;
    }
    openModeRef.current = 'normal';
    setOpenMode('normal');
    const ok = await loadInto(id, { coldStart: false });
    if (ok && shouldNavigate) void navigate('/play');
    return ok;
  }, [loadInto, navigate]);

  const setSelection = useCallback(async (next: LanguageSelection) => {
    const previous = selectionRef.current;
    if (next === previous) return;
    const target = resolveEffectiveLocale(next, deviceLanguages(), availableRef.current);
    const current = episodeRef.current;
    if (current && persistRef.current && episodeLocaleRef.current !== target) {
      try {
        const [loaded] = await Promise.all([loadEpisode(current.id, target, fallbackPolicy(next)), loadCatalog(target)]);
        if (episodeRef.current?.id === current.id) {
          episodeRef.current = loaded.content;
          episodeLocaleRef.current = target;
          setEp(loaded.content);
          setServed(loaded.servedLocale);
        }
      } catch {
        setLanguageError(tRef.current(LANGUAGE_UNAVAILABLE_MESSAGE));
        return; // rollback: the previous selection stays committed
      }
    }
    setLanguageError(undefined);
    selectionRef.current = next;
    writeSelection(next);
    setSel(next);
    track('language_selection_changed', { selection: next, from_selection: previous, effective_locale: target });
  }, []);

  const setPremium = useCallback((v: boolean) => {
    premiumRef.current = v;
    setPremiumState(v);
    const p = progressRef.current;
    // isPremium is never persisted (read from premium_entitlements), so no save is enqueued.
    if (p && p.isPremium !== v) applyProgress({ ...p, isPremium: v });
  }, [applyProgress]);

  const setEpisode = useCallback((e: EpisodeContent | undefined, served?: string) => {
    episodeRef.current = e;
    persistRef.current = false;
    setEp(e);
    setServed(served);
  }, []);

  const retryEpisodeLoad = useCallback(
    () => loadInto(episodeRef.current?.id ?? readSelectedEpisodeID(), { coldStart: !episodeRef.current }),
    [loadInto],
  );

  const value = useMemo<Session | null>(() => (auth && ready) ? {
    auth, locale, selection, availableLocales, t,
    setSelection,
    episode, progress, servedLocale,
    setEpisode,
    setProgress,
    isPremium, setPremium,
    episodeID, episodeState, episodeError,
    visibleEpisodeIDs: visibleIDs,
    selectEpisode,
    retryEpisodeLoad,
    reloadForLanguage,
    languageError,
    clearLanguageError: () => setLanguageError(undefined),
    shareProof,
    deliverStagedProof,
    proofFeedback,
    dismissProofFeedback: () => setProofFeedback(undefined),
    openMode, consumeOpenMode, openClientChat,
    saveState,
    saveErrorMessage: saveState === 'failed' ? t(SAVE_FAILED_MESSAGE) : undefined,
    dismissSaveError: () => setSaveState((s) => (s === 'failed' ? 'idle' : s)),
  } : null, [auth, ready, locale, selection, availableLocales, t, setSelection, episode, progress, servedLocale, setEpisode, setProgress, isPremium, setPremium, episodeID, episodeState, episodeError, visibleIDs, selectEpisode, retryEpisodeLoad, reloadForLanguage, languageError, shareProof, deliverStagedProof, proofFeedback, openMode, consumeOpenMode, openClientChat, saveState]);

  // The launch splash (BootOverlay) covers the screen until the session is ready and the first screen painted.
  if (error) return <BootReady screen><div className="stage flex h-full items-center justify-center p-6 text-center text-white"><p>{error}</p></div></BootReady>;
  // oxlint-disable-next-line react/refs -- false positive: `value` holds callbacks that read refs only when invoked, never during render.
  if (!value) return null;
  return <SessionContext.Provider value={value}><BootReady>{children}</BootReady></SessionContext.Provider>;
}

function BootReady({ children, screen = false }: { children: ReactNode; screen?: boolean }) {
  useEffect(() => { markSessionReady(); if (screen) markScreenMounted(); }, [screen]);
  return children;
}
